import React, { useState, useRef } from 'react';
import { Send, ImageIcon, Video as VideoIcon, BarChart3, Smile } from 'lucide-react';
import AudioRecorder from './AudioRecorder.tsx';

interface OptimizedMessageInputProps {
  onSendText: () => void;
  onSendImage: (file: File) => void;
  onSendVideo: (file: File) => void;
  onSendAudio?: (audioBlob: Blob, durationSeconds: number) => void;
  onSendNPS?: () => void;
  onRecordingStateChange?: (state: 'recording' | 'paused') => void;
  recordingContextKey?: string;
  disabled?: boolean;
  mediaDisabled?: boolean;
  textDisabled?: boolean;
  preserveDraftOnSubmit?: boolean;
  isWindowExpired?: boolean;
  onSendTemplate?: () => void;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onTextBlur?: () => void;
  extraControl?: React.ReactNode;
}

const EMOJIS_POPULARES = [
  '😊', '👍', '❤️', '😢', '😂', '🙏',
  '✅', '❌', '⚠️', '🎉', '💯', '🔥',
  '📞', '📅', '🦷', '💊', '🏥', '⭐',
];

export const OptimizedMessageInput: React.FC<OptimizedMessageInputProps> = ({
  onSendText,
  onSendImage,
  onSendVideo,
  onSendAudio,
  onSendNPS,
  onRecordingStateChange,
  recordingContextKey,
  disabled = false,
  mediaDisabled = false,
  textDisabled = false,
  preserveDraftOnSubmit = false,
  isWindowExpired = false,
  onSendTemplate,
  placeholder = 'Digite uma mensagem...',
  value = '',
  onChange,
  onTextBlur,
  extraControl,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
  };

  const insertEmoji = (emoji: string) => {
    onChange?.((value || '') + emoji);
    setShowEmojiPicker(false);
  };

  const handleSubmit = () => {
    if (value.trim() && !disabled && !textDisabled && !isWindowExpired) {
      onSendText();
      if (!preserveDraftOnSubmit) onChange?.('');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    if (disabled || mediaDisabled || isWindowExpired) return;
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    e.target.value = '';
    if (type === 'image') {
      onSendImage(file);
    } else {
      onSendVideo(file);
    }
  };

  const canSend = !!value.trim() && !disabled && !textDisabled && !isWindowExpired;

  // Estilo padrão dos action buttons (consistência)
  const actionBtnClass =
    'p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-sminy-primary disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center';

  return (
    <div className="relative flex items-center gap-1.5 px-3 py-2.5 bg-white border-t border-gray-200">
      {/* Emoji picker */}
      {showEmojiPicker && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowEmojiPicker(false)}
          />
          <div className="absolute bottom-full mb-2 left-3 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 z-50 animate-in fade-in zoom-in-95 duration-150 origin-bottom-left">
            <div className="grid grid-cols-6 gap-1">
              {EMOJIS_POPULARES.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => insertEmoji(emoji)}
                  className="text-xl hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
                  title={`Inserir ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Action buttons (esquerda) */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <AudioRecorder
          key={recordingContextKey || 'audio-recorder'}
          onAudioRecorded={(blob, durationSeconds) => { if (!disabled && !mediaDisabled && !isWindowExpired) onSendAudio?.(blob, durationSeconds); }}
          onRecordingStateChange={onRecordingStateChange}
          disabled={disabled || mediaDisabled || isWindowExpired}
        />

        <button
          onClick={() => setShowEmojiPicker((s) => !s)}
          disabled={disabled || isWindowExpired}
          className={`${actionBtnClass} ${showEmojiPicker ? 'bg-sminy-primary/10 text-sminy-primary' : ''}`}
          title="Emojis"
        >
          <Smile className="h-5 w-5" />
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || mediaDisabled || isWindowExpired}
          className={actionBtnClass}
          title="Enviar imagem"
        >
          <ImageIcon className="h-5 w-5" />
        </button>

        <button
          onClick={() => videoInputRef.current?.click()}
          disabled={disabled || mediaDisabled || isWindowExpired}
          className={actionBtnClass}
          title="Enviar vídeo"
        >
          <VideoIcon className="h-5 w-5" />
        </button>

        {onSendNPS && (
          <button
            onClick={onSendNPS}
            disabled={disabled}
            className={`${actionBtnClass} text-sminy-primary hover:text-sminy-primary`}
            title="Enviar pesquisa NPS"
          >
            <BarChart3 className="h-5 w-5" />
          </button>
        )}

        {extraControl}
      </div>

      {/* Campo de texto / aviso de janela expirada */}
      {isWindowExpired ? (
        <div className="flex-1 flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200">
          <span className="text-sm text-amber-700 font-medium truncate">Janela de 24h expirada</span>
          {onSendTemplate && (
            <button
              onClick={onSendTemplate}
              className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-xl hover:bg-amber-700 transition-colors font-semibold flex-shrink-0"
            >
              Enviar Template
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center min-h-[40px] rounded-xl border border-gray-200 bg-gray-50 transition-colors focus-within:border-sminy-primary focus-within:bg-white focus-within:ring-2 focus-within:ring-sminy-primary/20">
          <textarea
            value={value}
            onChange={handleTextChange}
            onBlur={onTextBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 px-3 py-2 max-h-32 min-h-[40px] resize-none focus:outline-none text-sm bg-transparent placeholder-gray-400 disabled:opacity-50"
            rows={1}
          />
        </div>
      )}

      {/* Send button */}
      {!isWindowExpired && (
        <button
          onClick={handleSubmit}
          disabled={!canSend}
          className={`flex-shrink-0 p-2.5 rounded-xl flex items-center justify-center transition-all ${
            canSend
              ? 'bg-sminy-primary text-white hover:bg-sminy-primary/90 hover:scale-105 shadow-sm shadow-sminy-primary/20'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          title="Enviar mensagem"
        >
          <Send className="h-5 w-5" />
        </button>
      )}

      {/* Inputs invisíveis */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileChange(e, 'image')}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFileChange(e, 'video')}
      />
    </div>
  );
};
